import type { MouseEvent } from 'react';
import {
  Box,
  Flex,
  InlineStack,
  Link,
  List,
  ListItem,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import type { IntegrationConnection } from '@/features/integrations/connections/view-model/types';
import { ConnectedItem, ConnectionIcon } from './connection-icon';
import * as styles from './styles.css';

type TooltipLinkClick = (connection: IntegrationConnection, event: MouseEvent) => void;

type ConnectionsLinkListProps = {
  connections: IntegrationConnection[];
  onClick: TooltipLinkClick;
  getFlowTypeLabel: (connection: IntegrationConnection) => string;
};

function ConnectionsLinkList({ connections, onClick, getFlowTypeLabel }: ConnectionsLinkListProps) {
  const firstConnectionFlowType = getFlowTypeLabel(connections[0]);
  const singleConnection = connections.length === 1 ? connections[0] : null;

  if (singleConnection != null) {
    return (
      <Box textAlign="left">
        <InlineStack gap="050" alignBlock="center">
          {firstConnectionFlowType}
          <Link
            isUnderlined
            variant="secondary"
            cursor="pointer"
            href={singleConnection.path}
            isExternal
            data-test-connection-link={singleConnection.id}
            onClick={(event) => onClick(singleConnection, event)}
          >
            {singleConnection.name}
          </Link>
        </InlineStack>
      </Box>
    );
  }

  return (
    <>
      <Box textAlign="left" mb="050" data-test-data-flow-label>
        {firstConnectionFlowType}
      </Box>
      <List size="xxs" textAlign="left">
        {connections.map((connection) => (
          <ListItem key={connection.id}>
            <Link
              isUnderlined
              variant="secondary"
              cursor="pointer"
              href={connection.path}
              isExternal
              data-test-connection-link={connection.id}
              onClick={(event) => onClick(connection, event)}
            >
              {connection.name}
            </Link>
          </ListItem>
        ))}
      </List>
    </>
  );
}

type ConnectionGroupProps = {
  connections: IntegrationConnection[];
  maxDisplay: number;
  getFlowTypeLabel: (connection: IntegrationConnection) => string;
  onTooltipLinkClick?: TooltipLinkClick;
  containerClass?: string;
};

export function ConnectionGroup({
  connections,
  maxDisplay,
  getFlowTypeLabel,
  onTooltipLinkClick,
  containerClass,
}: ConnectionGroupProps) {
  const autoTrackConnections = connections.filter((connection) => connection.isAutoTrack === true);
  const firstAutoTrackConnection = autoTrackConnections[0];
  const nonAutoTrackConnections = connections.filter((connection) => connection.isAutoTrack !== true);
  const hasAutoTrackConnections = autoTrackConnections.length > 0;
  const maxDisplayCount = maxDisplay - (hasAutoTrackConnections ? 1 : 0);
  const displayedConnections = nonAutoTrackConnections.slice(0, maxDisplayCount);
  const hasMoreConnections = nonAutoTrackConnections.length > maxDisplayCount;
  const additionalConnectionsCount = nonAutoTrackConnections.length - maxDisplayCount;
  const additionalConnections = nonAutoTrackConnections.slice(maxDisplayCount);

  const handleLinkClick: TooltipLinkClick = (connection, event) => {
    // No cross-app navigation helper in Hydra: when no click handler is
    // supplied, the Link's external href performs the navigation.
    onTooltipLinkClick?.(connection, event);
  };

  return (
    <Flex className={containerClass}>
      {firstAutoTrackConnection != null ? (
        <Tooltip placement="top">
          <ConnectionIcon connection={firstAutoTrackConnection} />
          <TooltipContent>
            <ConnectionsLinkList
              connections={autoTrackConnections}
              onClick={handleLinkClick}
              getFlowTypeLabel={getFlowTypeLabel}
            />
          </TooltipContent>
        </Tooltip>
      ) : null}

      {displayedConnections.map((connection) => (
        <ConnectedItem
          key={connection.id}
          connection={connection}
          flowTypeLabel={getFlowTypeLabel(connection)}
          onTooltipLinkClick={onTooltipLinkClick}
        />
      ))}

      {hasMoreConnections ? (
        <Tooltip placement="top">
          <Box className={styles.moreIndicator} data-test-more-connections>
            +{additionalConnectionsCount}
          </Box>
          <TooltipContent>
            <ConnectionsLinkList
              connections={additionalConnections}
              onClick={handleLinkClick}
              getFlowTypeLabel={getFlowTypeLabel}
            />
          </TooltipContent>
        </Tooltip>
      ) : null}
    </Flex>
  );
}
